/**
 * TeamMembersTable — single table that shows active members + pending
 * invites, mirroring `prototype/settings/settings-team.html ▸ Members card`.
 *
 * Active rows: avatar + name + email · role pill · phone · last active ·
 *              status pill · row actions (Change role · Revoke).
 * Pending rows: greyed avatar with initials · "(invite pending)" · role
 *               pill (muted) · phone · "Invited <date>" · status pill
 *               (warn/issue) · row actions (Resend ↻ · Cancel ×).
 *
 * Row actions trigger callbacks; the host (the route page) opens the
 * appropriate dialog or fires the mutation directly.
 */
import { Pencil, RotateCcw, X, UserMinus, Plus, User } from "lucide-react";
import { Button, StatusPill, cn } from "@flint/ui";
import { DataTableCard, ListPagination } from "../../master-list/index.ts";
import {
  formatPhoneIN,
  formatRelativeTime,
  formatDate,
  deriveInitials,
} from "../../../lib/format.ts";

export interface MemberRow {
  membershipId: string;
  userId: string;
  role: "Founder" | "Operator" | "Employee";
  /** Granular role (ADR-0030). Null for members not yet assigned a role —
   *  the Role pill falls back to the legacy `role` band then. */
  roleId: string | null;
  roleName: string | null;
  user: {
    name: string;
    phone: string;
    email: string | null;
    avatarUrl: string | null;
    lastSeenAt: string | null;
  };
}

export interface PendingInviteRow {
  inviteId: string;
  phone: string;
  name: string | null;
  role: "Founder" | "Operator" | "Employee";
  /** Granular role (ADR-0030). Null for legacy band-only invites — the pill
   *  falls back to the `role` band then. */
  roleId: string | null;
  roleName: string | null;
  invitedAt: string;
  expiresAt: string;
  isExpired: boolean;
}

export interface TeamMembersTableProps {
  members: ReadonlyArray<MemberRow>;
  pendingInvites: ReadonlyArray<PendingInviteRow>;
  /** Disable destructive actions while a mutation is in flight. */
  busy?: boolean;
  /** Active seats occupied (non-revoked memberships). */
  seatsUsed?: number | undefined;
  /** Plan seat ceiling. When `seatsUsed >= seatLimit`, the invite button is
   *  disabled with an upgrade prompt. */
  seatLimit?: number | undefined;
  onInvite: () => void;
  onChangeRole: (member: MemberRow) => void;
  onRevokeMembership: (member: MemberRow) => void;
  onResendInvite: (invite: PendingInviteRow) => void;
  onCancelInvite: (invite: PendingInviteRow) => void;
}

export function TeamMembersTable({
  members,
  pendingInvites,
  busy,
  seatsUsed,
  seatLimit,
  onInvite,
  onChangeRole,
  onRevokeMembership,
  onResendInvite,
  onCancelInvite,
}: TeamMembersTableProps) {
  const totalRows = members.length + pendingInvites.length;
  const atSeatLimit =
    seatsUsed != null && seatLimit != null && seatsUsed >= seatLimit;

  return (
    <div className="flex flex-col gap-4">
      {/* Header — matches prototype's `.form-card-head` pattern. */}
      <header className="flex items-start gap-3">
        <span
          aria-hidden
          className="inline-grid place-items-center h-8 w-8 rounded-sm bg-sky-50 text-sky-700 shrink-0"
        >
          <User size={14} strokeWidth={2} />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-ink leading-tight">Members</h2>
          <p className="mt-0.5 text-xs text-muted leading-relaxed">
            Each phone number can belong to one role per workspace. Phone change requires re-OTP.
          </p>
          {atSeatLimit ? (
            <p className="mt-1 text-xs text-warn font-medium">
              {seatsUsed}/{seatLimit} seats used — upgrade your plan to invite more members.
            </p>
          ) : null}
        </div>
        <Button
          intent="primary"
          size="sm"
          onClick={onInvite}
          disabled={atSeatLimit}
          title={atSeatLimit ? `All ${seatLimit} seats are occupied — upgrade your plan to add more members` : undefined}
        >
          <Plus size={14} strokeWidth={2.5} aria-hidden /> Invite member
        </Button>
      </header>

      <DataTableCard
        footer={
          totalRows > 0
            ? [
                { label: "Members", value: members.length },
                { label: "Pending invites", value: pendingInvites.length },
              ]
            : []
        }
        pagination={
          totalRows > 0 ? (
            <ListPagination
              pageNumber={1}
              showingFrom={totalRows === 0 ? 0 : 1}
              showingTo={totalRows}
              total={totalRows}
              sortLabel="sorted by role"
              hasPrev={false}
              hasNext={false}
              onPrev={() => {}}
              onNext={() => {}}
            />
          ) : undefined
        }
      >
        <table className="w-full text-sm">
          <thead className="bg-bg/50">
            <tr className="text-left text-xs uppercase tracking-wider text-muted">
              <th className="px-5 py-2.5 font-medium">Member</th>
              <th className="px-3 py-2.5 font-medium">Role</th>
              <th className="px-3 py-2.5 font-medium">Phone</th>
              <th className="px-3 py-2.5 font-medium">Last active</th>
              <th className="px-3 py-2.5 font-medium">Status</th>
              <th className="px-5 py-2.5 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {members.map((m) => (
              <MemberTr
                key={m.membershipId}
                member={m}
                busy={busy}
                onChangeRole={() => onChangeRole(m)}
                onRevokeMembership={() => onRevokeMembership(m)}
              />
            ))}
            {pendingInvites.map((i) => (
              <PendingTr
                key={i.inviteId}
                invite={i}
                busy={busy}
                onResend={() => onResendInvite(i)}
                onCancel={() => onCancelInvite(i)}
              />
            ))}
            {members.length === 0 && pendingInvites.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-5 py-10 text-center text-sm text-muted">
                  No members yet. Invite your first teammate to get started.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </DataTableCard>
    </div>
  );
}

// ── Row components ───────────────────────────────────────────────────

function MemberTr({
  member,
  busy,
  onChangeRole,
  onRevokeMembership,
}: {
  member: MemberRow;
  busy?: boolean | undefined;
  onChangeRole: () => void;
  onRevokeMembership: () => void;
}) {
  return (
    <tr className="hover:bg-bg/40">
      <td className="px-5 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar name={member.user.name} src={member.user.avatarUrl} />
          <div className="min-w-0">
            <div className="text-sm font-medium text-ink truncate">{member.user.name}</div>
            <div className="text-xs text-muted truncate">{member.user.email ?? "—"}</div>
          </div>
        </div>
      </td>
      <td className="px-3 py-3">
        <StatusPill tone="sky" size="sm">
          {member.roleName ?? member.role}
        </StatusPill>
      </td>
      <td className="px-3 py-3 num text-xs text-ink-2 whitespace-nowrap">
        {formatPhoneIN(member.user.phone)}
      </td>
      <td className="px-3 py-3 text-xs text-muted whitespace-nowrap">
        {member.user.lastSeenAt ? formatRelativeTime(member.user.lastSeenAt) : "—"}
      </td>
      <td className="px-3 py-3">
        <StatusPill tone="ok" size="xs" dot>
          Active
        </StatusPill>
      </td>
      <td className="px-5 py-3 text-right">
        <div className="inline-flex items-center gap-1">
          <Button
            intent="ghost"
            size="sm"
            onClick={onChangeRole}
            disabled={busy}
            title="Change role"
          >
            <Pencil size={13} strokeWidth={2} aria-hidden />
            <span className="hidden md:inline">Edit</span>
          </Button>
          <Button
            intent="ghost"
            size="sm"
            onClick={onRevokeMembership}
            disabled={busy}
            className="text-bad hover:bg-bad-50"
            title="Revoke membership"
          >
            <UserMinus size={13} strokeWidth={2} aria-hidden />
            <span className="sr-only">Revoke</span>
          </Button>
        </div>
      </td>
    </tr>
  );
}

function PendingTr({
  invite,
  busy,
  onResend,
  onCancel,
}: {
  invite: PendingInviteRow;
  busy?: boolean | undefined;
  onResend: () => void;
  onCancel: () => void;
}) {
  return (
    <tr className="opacity-70 hover:opacity-100 hover:bg-bg/40">
      <td className="px-5 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar name={invite.name ?? "? ?"} src={null} className="bg-warn/30 text-warn" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-ink truncate">
              {invite.name ?? "(invite pending)"}
            </div>
            <div className="text-xs text-muted truncate">
              Invited {formatDate(invite.invitedAt)}
            </div>
          </div>
        </div>
      </td>
      <td className="px-3 py-3">
        <StatusPill tone="muted" size="sm">
          {invite.roleName ?? invite.role}
        </StatusPill>
      </td>
      <td className="px-3 py-3 num text-xs text-ink-2 whitespace-nowrap">
        {formatPhoneIN(invite.phone)}
      </td>
      <td className="px-3 py-3 text-xs text-muted whitespace-nowrap">—</td>
      <td className="px-3 py-3">
        {invite.isExpired ? (
          <StatusPill tone="bad" size="xs" dot>
            Expired
          </StatusPill>
        ) : (
          <StatusPill tone="warn" size="xs" dot>
            Pending
          </StatusPill>
        )}
      </td>
      <td className="px-5 py-3 text-right">
        <div className="inline-flex items-center gap-1">
          <Button
            intent="ghost"
            size="sm"
            onClick={onResend}
            disabled={busy}
            title="Resend WhatsApp invite"
          >
            <RotateCcw size={13} strokeWidth={2} aria-hidden />
            <span className="sr-only">Resend</span>
          </Button>
          <Button
            intent="ghost"
            size="sm"
            onClick={onCancel}
            disabled={busy}
            className="text-bad hover:bg-bad-50"
            title="Cancel invite"
          >
            <X size={13} strokeWidth={2} aria-hidden />
            <span className="sr-only">Cancel</span>
          </Button>
        </div>
      </td>
    </tr>
  );
}

function Avatar({
  name,
  src,
  className,
}: {
  name: string;
  src: string | null;
  className?: string;
}) {
  return (
    <div
      aria-hidden
      className={cn(
        "inline-grid place-items-center h-9 w-9 rounded-sm bg-slate-700 text-paper text-xs font-bold tracking-tight select-none shrink-0 overflow-hidden",
        className,
      )}
    >
      {src ? <img src={src} alt="" className="h-full w-full object-cover" /> : deriveInitials(name)}
    </div>
  );
}
